<script setup lang="ts">
import { DialogModal, NoteComponent, type SharedBudget, type SharedBudgetPayload } from '@metapic-apps/shared'
import { computed, ref } from 'vue'

import { useDeleteSharedBudgetRequest, useEditSharedBudgetRequest, useGetSharedBudgets } from '@/api'
import AddPlusIcon from '@/assets/icons/add-plus.svg?component'
import DeleteIcon from '@/assets/icons/delete.svg?component'
import EditIcon from '@/assets/icons/edit.svg?component'
import SharedBudgetForm from '@/components/CampaignForm/SharedBudgetForm.vue'
import { formatMoneyForAdmin } from '@/lib/formatters/money.formatter'
import { notification } from '@/lib/helpers'
import type { CampaignFormType } from '@/lib/types/campaign.types'
import {
  BoxComponent,
  ButtonComponent,
  FlexComponent,
  ProgressComponent,
  SelectComponent,
  TitleComponent,
  TypographyComponent,
} from '@/ui-library'

const props = defineProps<{ currency?: string; advertiserId: number }>()
const sharedBudgetFormData = defineModel<CampaignFormType['sharedBudget']>('modelValue', { default: {} })
const emit = defineEmits(['update:modelValue', 'removeCurrentBudget'])

const { data, fetchData, isLoading } = useGetSharedBudgets(props.advertiserId)
const { editBudget, isLoading: isEditingLoading, error: editError } = useEditSharedBudgetRequest()
const { deleteBudget, isLoading: isDeletingLoading, error: deleteError } = useDeleteSharedBudgetRequest()

const value = computed(() => sharedBudgetFormData.value?.id || '')
const editingBudget = ref<SharedBudgetPayload>()
const isEditingPopupOpen = ref(false)
const isDeletePopupOpen = ref(false)
const createdBudgets = ref<SharedBudgetPayload[]>([])
const options = computed(() => createdBudgets.value.concat(data.value).map((item) => ({ label: item.title, ...item })))
const currentBudget = computed(() => options.value.find((budget) => budget.id === Number(value.value)))

const createSharedBudget = () => {
  editingBudget.value = {
    id: Math.random(),
    title: '',
    amount: { amount: 0, currency: props.currency || '' },
    currency: props.currency || '',
    consumedBudget: 0,
    campaignsCount: 0,
    isCreated: 'empty',
  }
  isEditingPopupOpen.value = true
}
const editSharedBudget = (item: SharedBudget) => {
  editingBudget.value = { ...item }
  isEditingPopupOpen.value = true
}
const handleSaveBudget = async (payload: SharedBudgetPayload) => {
  if (payload.isCreated) {
    const currentIndex = createdBudgets.value.findIndex((budget) => budget.id === payload.id)
    if (currentIndex !== -1) {
      createdBudgets.value[currentIndex] = { ...payload }
    } else {
      createdBudgets.value.push({ ...payload, isCreated: 'created' })
    }
  } else {
    await editBudget(props.advertiserId, payload)
  }

  isEditingPopupOpen.value = false
  editingBudget.value = undefined
  sharedBudgetFormData.value = { ...payload }
  if (!editError.value) {
    notification.success('The budget was saved successfully')
  }
  if (!payload.isCreated) {
    await fetchData()
  }
}
const openDeletionPopup = (item: SharedBudgetPayload) => {
  if (item.isCreated) {
    createdBudgets.value = createdBudgets.value.filter((budget) => budget.id !== item.id)
  } else {
    editingBudget.value = item
    isDeletePopupOpen.value = true
  }
}
const deleteSharedBudget = async () => {
  const deletedBudget = editingBudget.value as never as SharedBudgetPayload
  if (!deletedBudget.isCreated) {
    await deleteBudget(props.advertiserId, deletedBudget.id)
  } else {
    createdBudgets.value = createdBudgets.value.filter((budget) => budget.id !== deletedBudget.id)
  }

  if (deletedBudget.id === sharedBudgetFormData.value?.id) {
    emit('removeCurrentBudget')
  }

  isDeletePopupOpen.value = false
  editingBudget.value = undefined
  if (!deleteError.value) {
    notification.success('The budget was deleted successfully')
  }
  if (!deletedBudget.isCreated) {
    await fetchData()
  }
}

const handleItemSelect = (id: unknown) => emit('update:modelValue', id)
</script>
<template>
  <SelectComponent
    :model-value="value"
    :options="options"
    :is-loading="isLoading"
    value-key="id"
    placeholder="Select budget"
    @update:model-value="handleItemSelect"
  >
    <template #header>
      <BoxComponent :mb="8">
        <TitleComponent title="Shared Budget">
          <template #tooltip>
            <BoxComponent :w="280">
              <BoxComponent :mb="4">
                <TypographyComponent variant="title-medium">Shared budget</TypographyComponent>
              </BoxComponent>
              <TypographyComponent variant="body-medium">
                You can set a shared budget and add multiple campaigns to it. When creating new campaigns, simply select the same budget.
                Once the budget runs out, all assigned campaigns will automatically stop.
              </TypographyComponent>
            </BoxComponent>
          </template>
        </TitleComponent>
      </BoxComponent>
    </template>
    <template #label>
      <FlexComponent :gap="16" align="center">
        <template v-if="currentBudget">
          <BoxComponent mr="auto">
            <TypographyComponent variant="body-large">{{ currentBudget.title }}</TypographyComponent>
          </BoxComponent>
          <ProgressComponent
            :content="
              currentBudget.consumedBudget === undefined
                ? '*'
                : `${formatMoneyForAdmin({ amount: currentBudget.consumedBudget, currency: currentBudget.currency }, { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 })} / ${formatMoneyForAdmin({ amount: currentBudget.amount.amount, currency: currentBudget.currency }, { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
            "
            :tooltip-content="
              currentBudget.consumedBudget === undefined
                ? undefined
                : `${formatMoneyForAdmin({ amount: currentBudget.consumedBudget, currency: currentBudget.currency }, { allowZero: true })} / ${formatMoneyForAdmin({ amount: currentBudget.amount.amount, currency: currentBudget.currency }, { allowZero: true })}`
            "
            :percentage="
              currentBudget.consumedBudget === undefined
                ? 0
                : Math.ceil((Number(currentBudget.consumedBudget) / Number(currentBudget.amount.amount)) * 100)
            "
          />
        </template>
      </FlexComponent>
    </template>
    <template #option="slot">
      <FlexComponent :gap="16" align="center" class="shared-budget-selector__shared-budget-item">
        <BoxComponent mr="auto">
          <TypographyComponent variant="body-large">{{ slot.item.label }}</TypographyComponent>
          <TypographyComponent variant="body-small" color="text-muted">
            {{ slot.item.campaignsCount === 1 ? '1 campaign assigned' : `${slot.item.campaignsCount || 0} campaigns assigned` }}
          </TypographyComponent>
        </BoxComponent>
        <ProgressComponent
          :content="
            slot.item.consumedBudget === undefined
              ? '*'
              : `${formatMoneyForAdmin({ amount: slot.item.consumedBudget, currency: slot.item.currency }, { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 })} / ${formatMoneyForAdmin({ amount: slot.item.amount.amount, currency: slot.item.currency }, { allowZero: true, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
          "
          :tooltip-content="
            slot.item.consumedBudget === undefined
              ? undefined
              : `${formatMoneyForAdmin({ amount: slot.item.consumedBudget, currency: slot.item.currency }, { allowZero: true })} / ${formatMoneyForAdmin({ amount: slot.item.amount.amount, currency: slot.item.currency }, { allowZero: true })}`
          "
          :percentage="
            slot.item.consumedBudget === undefined ? 0 : Math.ceil((Number(slot.item.consumedBudget) / Number(slot.item.amount)) * 100)
          "
        />
        <FlexComponent :gap="8">
          <ButtonComponent variant="transparent" is-icon icon-color="var(--primary)" @click.stop.prevent="editSharedBudget(slot.item)">
            <EditIcon />
          </ButtonComponent>
          <ButtonComponent variant="transparent" icon-color="var(--error)" is-icon @click.stop.prevent="openDeletionPopup(slot.item)">
            <DeleteIcon />
          </ButtonComponent>
        </FlexComponent>
      </FlexComponent>
    </template>
    <template #footer>
      <div>
        <ButtonComponent class="shared-budget-selector__select-all w-100" variant="transparent" @click="createSharedBudget">
          <template #iconLeft>
            <el-icon :size="24" color="var(--primary)">
              <AddPlusIcon />
            </el-icon>
          </template>
          <TypographyComponent is="span" variant="body-large"> New Budget </TypographyComponent>
        </ButtonComponent>
      </div>
    </template>
  </SelectComponent>
  <SharedBudgetForm
    v-model="isEditingPopupOpen"
    :is-loading="isEditingLoading"
    :currency="currency"
    :budget="editingBudget"
    @save="handleSaveBudget"
  />
  <DialogModal v-if="isDeletePopupOpen" v-model="isDeletePopupOpen" @before-close="() => (isDeletePopupOpen = false)">
    <TypographyComponent variant="headline-medium">Delete shared budget</TypographyComponent>
    <BoxComponent v-if="editingBudget?.campaignsCount" :mt="16">
      <NoteComponent>There are running campaigns assigned to the budget.</NoteComponent>
    </BoxComponent>
    <BoxComponent :mt="16" :mb="24">
      <TypographyComponent variant="body-large">
        Are you sure to delete the budget? Campaigns will continue running without any limitation.
      </TypographyComponent>
    </BoxComponent>
    <FlexComponent :gap="8" justify="flex-end">
      <ButtonComponent variant="text" :is-disabled="isDeletingLoading" @click="() => (isDeletePopupOpen = false)">Cancel</ButtonComponent>
      <ButtonComponent variant="danger" :is-loading="isDeletingLoading" @click="deleteSharedBudget"> Delete campaign </ButtonComponent>
    </FlexComponent>
  </DialogModal>
</template>
<style lang="scss" scoped>
:deep(.el-icon) {
  path {
    fill: currentColor;
  }
}
.shared-budget-selector__select-all {
  display: flex;
  justify-content: start;
  align-items: center;
  padding: 16px;
  border: none;
  text-align: left;
  background: none;
  cursor: pointer;
  border-radius: 0 0 16px 16px;

  &:hover {
    background: var(--secondary-8);
  }
}
.shared-budget-selector__shared-budget-item {
  height: 40px;
}
</style>
